import { Routes, Route } from '@angular/router';
import { roleGuard } from 'src/lib/guards/admin-role.guard';
import { permissionGuard } from '../../../lib/guards/permission.guard';
import { featureGuard } from '../../../lib/guards/brand-feature.guard';
import { MRHST_CONFIGS } from './base/mrhst-configs';
import { MrhstConfig, MrhstConfigKey } from './base/mrhst.types';

/**
 * Mrhst Route Factory Function (Board 패턴)
 *
 * Config-Driven:
 * - mrhstType, mrhstSubType 자동 주입
 * - rxResource: Resolver 대체
 * - 10개 시설 유형을 단일 컴포넌트로 통합
 *
 * @param mrhstKey - 시설 설정 키
 * @param config - 시설 설정
 * @returns Route 객체
 */
function createMrhstRoute(
  mrhstKey: MrhstConfigKey,
  config: MrhstConfig,
): Route {
  return {
    path: config.routePath,
    canActivate: [
      ...(config.permissions?.map((p) => permissionGuard(p as any)) || []),
      ...(config.features?.map((f) => featureGuard(f as any)) || []),
      ...(config.roles?.map((r) => roleGuard(r)) || []),
    ],
    children: [
      {
        path: '',
        redirectTo: 'list',
        pathMatch: 'full',
      },
      {
        path: 'list',
        loadComponent: () =>
          import(
            './components/smart/mrhst-list-page/mrhst-list-page.component'
          ).then((m) => m.MrhstListPageComponent),
        data: {
          mrhstKey,
          config,
          title: getTitleByMrhstKey(mrhstKey),
        },
      },
    ],
  };
}

/**
 * 시설 키로 타이틀 반환
 */
function getTitleByMrhstKey(mrhstKey: MrhstConfigKey): string {
  const titles: Record<MrhstConfigKey, string> = {
    hotel: '숙박시설',
    car: '렌터카',
    attraction: '관광지',
    restaurant: '식당',
    guide: '가이드',
    farm: '농원',
  };
  return titles[mrhstKey];
}

/**
 * 통합 시설 라우팅
 *
 * AS-IS (Legacy):
 * - 7개 별도 모듈 + 3개 ETC 서브타입
 * - 10개 별도 라우팅 (~200줄)
 *
 * TO-BE (Modern):
 * - 1개 Smart 컴포넌트 (MrhstListPageComponent)
 * - Config-Driven 동적 라우팅 (~80줄)
 * - rxResource → 즉시 라우트 전환
 */
export const mrhstRoutes: Routes = (
  Object.keys(MRHST_CONFIGS) as MrhstConfigKey[]
).map((key) => createMrhstRoute(key, MRHST_CONFIGS[key]));
